import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Tooltip, makeStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DescriptionIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';
import PersonIcon from '@material-ui/icons/Person';
import cx from 'classnames';
import {
  LOGIN_URL,
  TEACHERS_ASSIGNMENTS_DETAIL_URL,
  TEACHERS_HOME_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_V2_LIST_URL
} from 'config/urls';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { DATE_FORMATS } from 'constants/dateFormats';
import { get, isNil } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { getLiveClassroomInsightsExportURL, logout } from 'sdk';
import { cutText, pluralize } from 'utils';

import { ReactComponent as ExportIcon } from 'pages/Teachers/ClassroomInsights/assets/export-icon.svg';
import { ReactComponent as PartialUploadIcon } from 'pages/Teachers/ClassroomInsights/assets/partial-upload-icon.svg';
import { ReactComponent as PendingUploadIcon } from 'pages/Teachers/ClassroomInsights/assets/pending-upload-icon.svg';
import { ReactComponent as ReuploadIcon } from 'pages/Teachers/ClassroomInsights/assets/reupload-icon.svg';
import AccountMenuButton from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/components/AccountMenuButton';
import HelpButton from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/components/HelpButton';
import { colors } from 'theme/palette';
import { formatDate } from 'utils/date';
import { shutdownIntercomWidget } from 'utils/integrations/intercom';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { default as pusherManager } from 'utils/integrations/pusher';
import { parseParams, stringifyParams } from 'utils/urls';
import { openInNewTab } from 'utils/urls';
import { reverse } from 'utils/urls';

import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';
const ELA_INSIGHTS_ENABLED =
  process.env.REACT_APP_ELA_INSIGHTS_ENABLED === 'true';

const tooltipUseStyles = makeStyles({
  customTooltip: {
    backgroundColor: '#333867',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Nunito'
  }
});

const Header = ({
  data,
  dataIsLoading,
  aboveCount,
  atCount,
  belowCount,
  needsReviewCount,
  loadingCount,
  onPlusButtonClick
}) => {
  const history = useHistory();

  const { trackerInstanceSectionId } = useParams();
  const user = useContext(UserContext);
  const tooltipStyles = tooltipUseStyles();
  const searchParams = parseParams(history.location.search);
  const currentPage = get(searchParams, 'currentPage');
  const assignmentsPage = get(searchParams, 'assignmentsPage');
  const sectionId = get(searchParams, 'sectionId');

  const logoutAndRedirect = async () => {
    shutdownIntercomWidget();
    pusherManager.disconnect();
    await logout().then(() => {
      history.push(LOGIN_URL);

      if (process.env.REACT_APP_MIXPANEL_KEY) {
        mixpanel.reset();
      }
    });
  };

  useEffect(() => {
    if (isNil(data)) return;

    if (
      ELA_INSIGHTS_ENABLED &&
      !data.is_ela_assignment &&
      !data.is_math_assignment
    ) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }

    if (!ELA_INSIGHTS_ENABLED && !data.is_math_assignment) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }
  }, [data, history]);

  const handleBackButtonClick = useCallback(() => {
    // TODO: Clear home page enable logic
    if (HOME_PAGE_ENABLED) {
      if (assignmentsPage) {
        history.push({
          pathname: reverse(TEACHER_ASSIGNMENT_V2_LIST_URL, {
            sectionId
          }),
          search: stringifyParams({ currentPage: assignmentsPage })
        });
        return;
      }

      history.push({
        pathname: TEACHERS_HOME_URL,
        search: stringifyParams({ currentPage })
      });
    } else {
      history.push(TEACHER_ASSIGNMENTS_V2_URL);
    }
  }, [history, currentPage, assignmentsPage, sectionId]);

  const handleAssignmentEditClick = (event) => {
    event.stopPropagation();
    history.push({
      pathname: reverse(TEACHERS_ASSIGNMENTS_DETAIL_URL, {
        trackerId: data?.tracker_id
      })
    });
  };

  const submitted =
    aboveCount + atCount + belowCount + needsReviewCount + loadingCount;

  const exportResults = useCallback(() => {
    openInNewTab(
      getLiveClassroomInsightsExportURL({ trackerInstanceSectionId })
    );
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'live classroom insights',
        view: 'classroom insights header',
        button: 'export data',
        class_id: data.section_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      }
    );
  }, [trackerInstanceSectionId, user.id, data?.section_id]);

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.leftPart}>
          <IconButton onClick={handleBackButtonClick}>
            <ArrowBackIcon />
          </IconButton>
          <div className={styles.header}>
            {dataIsLoading && <CircularProgress size={20} />}
            {!dataIsLoading && (
              <>
                <Typography
                  variant="H-TEXT-1"
                  color={colors.blue1}
                  style={{ textTransform: 'none' }}
                >
                  {data?.tracker_name}
                </Typography>

                <div className={styles.meta}>
                  <Tooltip
                    arrow
                    classes={{ tooltip: tooltipStyles.customTooltip }}
                    title={
                      data?.section_name?.length > 40 ? (
                        <Typography variant="B-Text-2">
                          {data?.section_name}
                        </Typography>
                      ) : (
                        ''
                      )
                    }
                  >
                    <span>
                      {data?.section_name?.length > 40
                        ? cutText({ text: data?.section_name, char: 40 })
                        : data?.section_name}
                    </span>
                  </Tooltip>
                  <div>
                    <DescriptionIcon />
                    {data?.pages_count} Pages
                  </div>
                  <div>
                    <DateRangeIcon />
                    {data?.due_at &&
                      formatDate({
                        date: data?.due_at,
                        format: DATE_FORMATS.MEDIUM,
                        fallback: ''
                      })}
                  </div>
                  <div>
                    <PersonIcon />
                    {data?.students_count} students
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.rightPart}>
          <div className={styles.actionsWrapper}>
            <HelpButton user={user?.user} />
            <AccountMenuButton user={user?.user} logout={logoutAndRedirect} />
          </div>
        </div>
      </div>
      <div className={styles.statuses}>
        <div className={styles.statusBox}>
          <b>{aboveCount}</b>{' '}
          <Typography variant="S-TEXT-1">Celebrate</Typography>
        </div>
        <div className={cx(styles.statusBox, styles.yellowBox)}>
          <b>{atCount}</b> <Typography variant="S-TEXT-1">Support</Typography>
        </div>
        <div className={cx(styles.statusBox, styles.redBox)}>
          <b>{belowCount}</b>
          <Typography variant="S-TEXT-1">Intervene</Typography>
        </div>
        <Tooltip
          title={
            needsReviewCount >= 1
              ? 'AI was unable to fully process these students work due to missing, blurry, or unreadable content. Teacher review is needed to confirm mastery.'
              : ''
          }
          classes={{ tooltip: tooltipStyles.customTooltip }}
        >
          <div className={styles.reviewState}>
            <ReuploadIcon />
            <Typography variant="S-TEXT-1">
              <b>({needsReviewCount})</b> Reupload Needed
            </Typography>
          </div>
        </Tooltip>

        <div className={styles.studentUploads}>
          <PendingUploadIcon />
          <Typography variant="S-TEXT-1">
            {submitted}/{data?.students_count} Uploads
          </Typography>
        </div>
        <div className={styles.studentUploads}>
          <PartialUploadIcon />
          <Typography variant="S-TEXT-1">
            {data?.partial_upload_students_count}{' '}
            {pluralize({
              noun: 'Partial Upload',
              count: data?.partial_upload_students_count
            })}
          </Typography>
        </div>
        <div className={styles.buttons}>
          <Tooltip
            title="Edit Assignment"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton
              className={styles.addButton}
              onClick={handleAssignmentEditClick}
            >
              <EditIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Upload More Work"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton
              className={styles.addButton}
              onClick={onPlusButtonClick}
            >
              <AddIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Export Score"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton onClick={exportResults}>
              <ExportIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Header;
